// packages/web/src/hooks/useCatalog.ts
// Catalog detection and management hook

import { useEffect, useState } from 'react';

import {
  clearUserCatalogCache,
  detectCatalogInstance,
  getUserActiveCatalogId,
  hasAuthTokens,
} from '../lib/catalog';
import type { CatalogContext, CatalogInstance } from '../lib/catalog/types';

/**
 * Hook for catalog detection and management
 */
export function useCatalog() {
  const [catalog, setCatalog] = useState<CatalogInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadCatalog = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('[USE-CATALOG] Starting catalog detection...');
        const detectedCatalog = await detectCatalogInstance();
        console.log('[USE-CATALOG] Raw detected catalog:', detectedCatalog);

        const catalogInstance = (detectedCatalog as any)?.catalogInstance || detectedCatalog;
        console.log('[USE-CATALOG] Processed catalog instance:', {
          id: catalogInstance?.id,
          name: catalogInstance?.name,
          isMainCatalog: catalogInstance?.isMainCatalog,
        });

        if (mounted) {
          setCatalog(catalogInstance);
          console.log(
            '[USE-CATALOG] Catalog state updated, catalogId will be:',
            catalogInstance?.id,
          );
        }
      } catch (err) {
        console.error('[USE-CATALOG] Error detecting catalog:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to detect catalog');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      mounted = false;
    };
  }, []);

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);

      const detectedCatalog = await detectCatalogInstance();
      const catalogInstance = (detectedCatalog as any)?.catalogInstance || detectedCatalog;
      setCatalog(catalogInstance);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh catalog');
    } finally {
      setLoading(false);
    }
  };

  const clearCache = () => {
    clearUserCatalogCache();
  };

  return {
    catalog,
    loading,
    error,
    refresh,
    clearCache,
    isMainCatalog: catalog?.isMainCatalog ?? false,
    catalogId: catalog?.id,
    catalogName: catalog?.name,
  };
}

/**
 * Get catalog instance ID for API calls
 * Priority: User preference (if authenticated) > URL detection > main catalog
 */
export async function getCatalogInstanceId(): Promise<string | undefined> {
  // Check if user is authenticated before trying to get user preferences
  try {
    if (hasAuthTokens()) {
      const userPreference = await getUserActiveCatalogId();
      if (userPreference) {
        return userPreference;
      }
    }
  } catch (error) {
    console.error('[CATALOG-HOOK] Failed to get user catalog preference:', error);
  }

  // Fallback to URL-based detection
  const catalog = await detectCatalogInstance();
  const catalogInstance = (catalog as any)?.catalogInstance || catalog;

  if (catalogInstance?.id) {
    return catalogInstance.id;
  }

  // Final fallback: try to get main catalog
  try {
    const mainCatalog = await getMainCatalogId();
    if (mainCatalog) {
      return mainCatalog;
    }
  } catch (error) {
    console.error('[CATALOG-HOOK] Failed to get main catalog:', error);
  }

  return undefined;
}

/**
 * Get catalog context for API requests
 * Priority: User preference (if authenticated) > URL detection > fallback
 */
export async function getCatalogContext(): Promise<CatalogContext> {
  // Check if user is authenticated before trying to get user preferences
  try {
    if (hasAuthTokens()) {
      console.log(
        '[CATALOG-HOOK] User appears authenticated, checking catalog context preferences',
      );
      const userPreference = await getUserActiveCatalogId();
      if (userPreference) {
        // Get catalog details for the preferred catalog
        const catalog = await detectCatalogInstance();
        const catalogInstance = (catalog as any)?.catalogInstance || catalog;
        return {
          catalogInstanceId: userPreference,
          isMainCatalog: catalogInstance?.isMainCatalog || false,
        };
      }
    } else {
      console.log('[CATALOG-HOOK] User appears anonymous, skipping context preference lookup');
    }
  } catch (error) {
    console.warn(
      '[CATALOG-HOOK] Failed to get user catalog context, falling back to URL detection:',
      error,
    );
  }

  // Fallback to URL detection
  const catalog = await detectCatalogInstance();
  const catalogInstance = (catalog as any)?.catalogInstance || catalog;
  if (!catalogInstance) return {};

  return {
    catalogInstanceId: catalogInstance.id,
    isMainCatalog: catalogInstance.isMainCatalog,
  };
}

/**
 * Check if current catalog is a partner catalog
 */
export async function isPartnerCatalog(): Promise<boolean> {
  const catalog = await detectCatalogInstance();
  const catalogInstance = (catalog as any)?.catalogInstance || catalog;
  return catalogInstance ? !catalogInstance.isMainCatalog : false;
}

/**
 * Get catalog display name
 * Priority: User preference (if authenticated) > URL detection > fallback
 */
export async function getCatalogDisplayName(): Promise<string> {
  // Check if user is authenticated before trying to get user preferences
  try {
    if (hasAuthTokens()) {
      // First, try to get the user preference directly
      const userPreference = await getUserActiveCatalogId();

      if (userPreference) {
        // Try to get catalog details from the marketplace API (more reliable)
        try {
          const { apiFetch } = await import('../lib/api');
          const catalogsResponse = await apiFetch('/api/marketplace/catalogs', { method: 'GET' });
          const catalogs = catalogsResponse.catalogs || [];

          if (catalogs.length > 0) {
            const selectedCatalog = catalogs.find((c: any) => c.id === userPreference);
            if (selectedCatalog) {
              return selectedCatalog.name;
            }
          }
        } catch (marketplaceApiError) {
          // Ignore marketplace API errors
        }

        // Fallback: Try customer API
        try {
          const { apiFetch } = await import('../lib/api');
          const data = await apiFetch('/api/marketplace/customers/catalogs', { method: 'GET' });

          const catalogs = data.catalogs || [];

          if (catalogs.length > 0) {
            const selectedCatalog = catalogs.find((c: any) => c.id === userPreference);
            if (selectedCatalog) {
              return selectedCatalog.name;
            }
          }
        } catch (customersApiError) {
          // Ignore customers API errors
        }
      }
    }
  } catch (error) {
    // Ignore all errors in display name lookup
  }

  // Fallback to URL detection
  const catalog = await detectCatalogInstance();
  const catalogInstance = (catalog as any)?.catalogInstance || catalog;
  return catalogInstance?.name || 'TechAdds Marketplace';
}

/**
 * Get the main TechAdds catalog instance ID
 */
async function getMainCatalogId(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  try {
    const response = await fetch('/api/marketplace/catalogs?limit=1&mainOnly=true', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      const data = await response.json();
      const mainCatalog = data.catalogs?.find((c: any) => c.is_main_catalog);
      return mainCatalog?.id || null;
    }
  } catch (error) {
    console.warn('[CATALOG-HOOK] Failed to fetch main catalog:', error);
  }

  return null;
}
